import { z } from 'zod';

import { boolFromQuery } from './_query';
import { ActorKindSchema, ActorProvenanceSchema } from './provenance.dto';

const TaskTitle = z.string().min(1).max(200);
const TaskTypeId = z.string().min(1).max(40);
const StatusId = z.string().min(1).max(80); // a workflow state key (≤ 80)
const ColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const Priority = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
const DescriptionFormat = z.enum(['markdown', 'html', 'plain']);

/**
 * Accepts either a calendar-day string (`YYYY-MM-DD`) or a full ISO 8601
 * datetime (`YYYY-MM-DDTHH:mm[:ss[.sss]]Z?` etc.).  The deadline endpoints
 * use this for `dueDate` — date-only inputs are resolved to 23:59:59 in the
 * project's tz on the server side; datetime inputs are stored as-is.
 */
const IsoDateOrDateTime = z
  .string()
  .min(10)
  .max(40)
  .regex(
    /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/,
    'Expected YYYY-MM-DD or ISO 8601 datetime',
  );

// Declared early so CreateTaskSchema can reference it for the optional
// inline `relations[]` field.  See `// ── relations` section below for the
// full set of relation-related schemas (mirrors, history, etc.).
/**
 * Phase 5 chunk 6 — Incident severity scale.
 *   sev1 = critical (outage / data loss)
 *   sev2 = high     (major degradation)
 *   sev3 = medium   (minor issue)
 *   sev4 = low      (cosmetic / non-urgent)
 */
export const IncidentSeverity = z.enum(['sev1', 'sev2', 'sev3', 'sev4']);
export type IncidentSeverityValue = z.infer<typeof IncidentSeverity>;

/**
 * One slot in `tasks.ownership_chain`.  Chunk 6 supports two roles —
 * the escalation chunk (8) will broaden the enum.
 */
export const IncidentOwnershipRole = z.enum(['primary', 'escalation_1']);
export type IncidentOwnershipRoleValue = z.infer<typeof IncidentOwnershipRole>;

export const IncidentOwnershipSlotSchema = z.object({
  userId: z.string().uuid(),
  role: IncidentOwnershipRole,
  /** ISO-8601 datetime — when this slot took over. */
  since: z.string().datetime(),
});
export type IncidentOwnershipSlot = z.infer<typeof IncidentOwnershipSlotSchema>;

export const RelationType = z.enum([
  'LINK',
  'BLOCKS',
  'BLOCKED_BY',
  'RELATES_TO',
  'DUPLICATES',
  'DUPLICATED_BY',
  'PARENT_OF',
  'CHILD_OF',
  'PRECEDES',
  'FOLLOWS',
]);
export type RelationTypeValue = z.infer<typeof RelationType>;

/** Tag pill shared between TaskSchema + TaskListItemSchema.  Compact shape:
 *  the picker / settings UI loads the full Tag projection separately. */
export const TaskListTagSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
});
export type TaskListTag = z.infer<typeof TaskListTagSchema>;

/** Heuristic Risk Score — Phase 1 feature #7.  Server computes via
 *  `RiskScoreService` in the task hydration step; surfaces consume the
 *  numeric score + bucketed level + human reasons without re-deriving
 *  client-side.  Optional in projections (null = not computed for
 *  done/cancelled/archived). */
export const RiskLevel = z.enum(['none', 'low', 'medium', 'high']);
export type RiskLevelValue = z.infer<typeof RiskLevel>;

export const RiskScoreSchema = z.object({
  score: z.number().int().min(0).max(100),
  level: RiskLevel,
  reasons: z.array(z.string()),
});
export type RiskScoreProjection = z.infer<typeof RiskScoreSchema>;

// ── create ──────────────────────────────────────────────────────────────────
export const CreateTaskSchema = z.object({
  title: TaskTitle,
  description: z.unknown().optional(), // TipTap JSON
  descriptionText: z.string().max(60_000).optional(),
  descriptionFormat: DescriptionFormat.default('markdown'),
  type: TaskTypeId.default('task'),
  /** Resolved status id within the project's resolved status set. */
  statusId: StatusId.optional(), // default → project's first status by `order`
  priority: Priority.default(2),
  /** Optional explicit assignment overrides — when omitted, project's
   * `default_task_assignee_strategy` decides. */
  respondentId: z.string().uuid().nullable().optional(),
  assigneeId: z.string().uuid().nullable().optional(),
  parentTaskId: z.string().uuid().nullable().optional(),
  /** Optional milestone binding at create time — saves a follow-up PATCH. */
  milestoneId: z.string().uuid().nullable().optional(),
  startDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  /** Deadline — either a date (YYYY-MM-DD, resolved to 23:59:59 in the
   *  project's tz by the server) or a full ISO datetime ("…T15:00:00Z").
   *  Internally stored as TIMESTAMPTZ; the projection always returns an
   *  ISO datetime string. */
  dueDate: IsoDateOrDateTime.nullable().optional(),
  laborHours: z.number().nonnegative().max(99999).optional(),
  /** Optional initial tag attachments — written to `task_tag_links` in the
   *  same tx as the task INSERT.  Tag must already exist in the project's
   *  pool (use POST /projects/.../tags first to create new ones). */
  tagIds: z.array(z.string().uuid()).max(20).optional(),
  /** Optional initial relations — same tx as the task INSERT.  Mirrors get
   *  written by the service.  Cycle-checks for BLOCKS still apply. */
  relations: z
    .array(
      z.object({
        targetTaskId: z.string().uuid(),
        type: RelationType,
      }),
    )
    .max(20)
    .optional(),
});
export type CreateTaskInput = z.infer<typeof CreateTaskSchema>;

// ── update (partial PATCH — live edit pattern) ──────────────────────────────
/**
 * Partial PATCH body for live-editing a task.  Every field is optional.  The endpoint
 * runs per-field permission checks: a forbidden field returns 403 with the breakdown
 * (which scopes were missing).
 *
 * Per founder's edit-pattern policy (memory: project_edit_pattern_policy):
 * the frontend sends one field at a time during live editing (autosave); this same
 * schema accepts multi-field bodies for the rare case (e.g. set respondent + assignee
 * in one click).  No Idempotency-Key required on this PATCH — TanStack Query handles
 * in-flight cancellation client-side.
 */
export const UpdateTaskSchema = z
  .object({
    title: TaskTitle.optional(),
    description: z.unknown().nullable().optional(),
    descriptionText: z.string().max(60_000).nullable().optional(),
    descriptionFormat: DescriptionFormat.optional(),
    type: TaskTypeId.optional(),
    priority: Priority.optional(),
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
    dueDate: IsoDateOrDateTime.nullable().optional(),
    laborHours: z.number().nonnegative().max(99999).optional(),
    parentTaskId: z.string().uuid().nullable().optional(),
    /** Phase 5 chunk 6 — Incident toggle + metadata.  Service clears
     *  severity + slaAt automatically when isIncident flips back to
     *  false to keep the DB CHECK constraint happy. */
    isIncident: z.boolean().optional(),
    severity: IncidentSeverity.nullable().optional(),
    slaAt: z.string().datetime().nullable().optional(),
    ownershipChain: z.array(IncidentOwnershipSlotSchema).max(10).optional(),
  })
  .strict();
export type UpdateTaskInput = z.infer<typeof UpdateTaskSchema>;

/**
 * Status / people endpoints stay separate from PATCH because they carry hierarchy +
 * override + Done-special-case logic the generic PATCH would conflate.
 */
export const ChangeStatusSchema = z.object({
  statusId: StatusId,
});
export type ChangeStatusInput = z.infer<typeof ChangeStatusSchema>;

export const SetRespondentSchema = z.object({
  userId: z.string().uuid().nullable(),
});
export type SetRespondentInput = z.infer<typeof SetRespondentSchema>;

export const SetAssigneeSchema = z.object({
  userId: z.string().uuid().nullable(),
});
export type SetAssigneeInput = z.infer<typeof SetAssigneeSchema>;

export const SetCollaboratorsSchema = z.object({
  userIds: z.array(z.string().uuid()).max(50),
});
export type SetCollaboratorsInput = z.infer<typeof SetCollaboratorsSchema>;

export const AddWatcherSchema = z.object({
  userId: z.string().uuid().optional(), // omit → self-watch
});
export type AddWatcherInput = z.infer<typeof AddWatcherSchema>;

/** Batch replace of a task's watcher set.  Atomicity matters more here than
 *  for collaborators because watchers gate notifications — a half-applied
 *  set could surprise users on the next event fanout. */
export const SetWatchersSchema = z.object({
  userIds: z.array(z.string().uuid()).max(50),
});
export type SetWatchersInput = z.infer<typeof SetWatchersSchema>;

// ── relations ───────────────────────────────────────────────────────────────
// `RelationType` is declared near the top so CreateTaskSchema can use it.

export const AddRelationSchema = z.object({
  targetTaskId: z.string().uuid(),
  type: RelationType,
  description: z.string().max(500).optional(),
});
export type AddRelationInput = z.infer<typeof AddRelationSchema>;

export const RelationSchema = z.object({
  id: z.string().uuid(),
  sourceTaskId: z.string().uuid(),
  targetTaskId: z.string().uuid(),
  type: RelationType,
  description: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string(),
});
export type RelationProjection = z.infer<typeof RelationSchema>;

// ── tags ────────────────────────────────────────────────────────────────────
const TagName = z.string().min(1).max(40);
const TagColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/);

export const CreateTagSchema = z.object({
  name: TagName,
  color: TagColorHex.default('#94a3b8'),
  description: z.string().max(200).optional(),
  categoryId: z.string().uuid().nullable().optional(),
  displayOrder: z.number().int().nonnegative().optional(),
});
export type CreateTagInput = z.infer<typeof CreateTagSchema>;

export const UpdateTagSchema = z
  .object({
    name: TagName.optional(),
    color: TagColorHex.optional(),
    description: z.string().max(200).nullable().optional(),
    categoryId: z.string().uuid().nullable().optional(),
    displayOrder: z.number().int().nonnegative().optional(),
  })
  .strict();
export type UpdateTagInput = z.infer<typeof UpdateTagSchema>;

export const TagSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  categoryId: z.string().uuid().nullable(),
  name: z.string(),
  color: z.string(),
  description: z.string().nullable(),
  displayOrder: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type TagProjection = z.infer<typeof TagSchema>;

export const AttachTagsSchema = z.object({
  tagIds: z.array(z.string().uuid()).min(1).max(50),
});
export type AttachTagsInput = z.infer<typeof AttachTagsSchema>;

// ── tag categories ──────────────────────────────────────────────────────────
export const CreateTagCategorySchema = z.object({
  name: z.string().min(1).max(40),
  color: TagColorHex.default('#94a3b8'),
  displayOrder: z.number().int().nonnegative().optional(),
});
export type CreateTagCategoryInput = z.infer<typeof CreateTagCategorySchema>;

export const UpdateTagCategorySchema = z
  .object({
    name: z.string().min(1).max(40).optional(),
    color: TagColorHex.optional(),
    displayOrder: z.number().int().nonnegative().optional(),
  })
  .strict();
export type UpdateTagCategoryInput = z.infer<typeof UpdateTagCategorySchema>;

export const TagCategorySchema = z.object({
  id: z.string().uuid(),
  teamId: z.string().uuid(),
  name: z.string(),
  color: z.string(),
  displayOrder: z.number().int().nonnegative(),
});
export type TagCategoryProjection = z.infer<typeof TagCategorySchema>;

// ── drag targets ────────────────────────────────────────────────────────────
const StatusCategory = z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']);
export const DragTargetsSchema = z.object({
  /** Categories the actor CAN drop the task into.  Frontend uses this to highlight columns. */
  allowedCategories: z.array(StatusCategory),
  /** Categories explicitly blocked for the actor, with the reason code. */
  blockedCategories: z.array(
    z.object({
      category: StatusCategory,
      reason: z.enum(['no_scope', 'override_blocked', 'archived', 'time_tracking_required']),
      message: z.string(),
    }),
  ),
});
export type DragTargetsProjection = z.infer<typeof DragTargetsSchema>;

// ── history ─────────────────────────────────────────────────────────────────
export const HistoryEntrySchema = z.object({
  id: z.string(), // BIGSERIAL → string
  taskId: z.string().uuid(),
  actorUserId: z.string().uuid(),
  action: z.string(), // 37-action enum (string for forward-compat)
  oldValue: z.unknown().nullable(),
  newValue: z.unknown().nullable(),
  description: z.string().nullable(),
  createdAt: z.string(),
  // Provenance — who really did this (default 'user' for normal/legacy rows).
  actorKind: ActorKindSchema.default('user'),
  /** Agent definition id when actorKind === 'agent' (soft ref — may dangle if the agent was later deleted). */
  agentId: z.string().uuid().nullable(),
  /** Resolved agent display name (null if no agent or the agent was deleted). */
  agentName: z.string().nullable(),
  /** Extra provenance: agentRunId / triggerType / aiActionLogId / onBehalfOf. */
  provenance: ActorProvenanceSchema.nullable(),
});
export type HistoryEntryProjection = z.infer<typeof HistoryEntrySchema>;

export const HistoryPageSchema = z.object({
  items: z.array(HistoryEntrySchema),
  nextCursor: z.string().nullable(),
});
export type HistoryPageProjection = z.infer<typeof HistoryPageSchema>;

// ── lifecycle (archive / restore / permanent-delete) ─────────────────────────
export const DeletePermanentlySchema = z.object({
  /** Must equal `${project.key}-${taskNumber}` exactly (e.g. "WEB-123"). */
  confirmKey: z.string().regex(/^[A-Z][A-Z0-9]{0,9}-\d{1,9}$/),
});
export type DeletePermanentlyInput = z.infer<typeof DeletePermanentlySchema>;

// ── reminder ────────────────────────────────────────────────────────────────
export const SetReminderSchema = z.object({
  at: z.string().datetime().nullable(),
});
export type SetReminderInput = z.infer<typeof SetReminderSchema>;

// ── pickers (search / recent / eligible-people) ─────────────────────────────
export const TaskSearchQuerySchema = z.object({
  q: z.string().max(120).optional(),
  /** CSV list of project ids the search is scoped to.  Empty → all accessible projects. */
  projectIds: z.string().optional(),
  /** CSV list of task ids to exclude (e.g. self when picking a parent). */
  excludeIds: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(15),
});
export type TaskSearchQuery = z.infer<typeof TaskSearchQuerySchema>;

export const RecentTasksQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(10),
});
export type RecentTasksQuery = z.infer<typeof RecentTasksQuerySchema>;

export const EligiblePeopleQuerySchema = z.object({
  q: z.string().max(80).optional(),
  /** CSV list of user ids to exclude (e.g. already-selected members). */
  excludeUserIds: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type EligiblePeopleQuery = z.infer<typeof EligiblePeopleQuerySchema>;

export const EligiblePersonSchema = z.object({
  user: z.object({
    id: z.string().uuid(),
    username: z.string(),
    name: z.string(),
    avatarUrl: z.string().nullable(),
  }),
  /**
   * TRUE iff the user already has access to this project.  When FALSE, the picker UI marks
   * "Adding will grant project access" — selecting them in an assignee field auto-creates a
   * project_access row.
   */
  hasProjectAccess: z.boolean(),
  rolePreset: z.enum(['OWNER', 'MANAGER', 'LEAD', 'MEMBER', 'OUTSTAFF', 'VIEWER']).nullable(),
});
export type EligiblePersonProjection = z.infer<typeof EligiblePersonSchema>;

// ── comments ────────────────────────────────────────────────────────────────

/** One attachment on a comment.  Mirrors the shape used by chat-message
 *  attachments (file / image / voice).  task-ref attachments live in the
 *  body's TipTap mentions, not here, so no `task_ref` kind. */
export const CommentAttachmentInputSchema = z
  .object({
    /** File row id already uploaded via `/api/files/presign`. */
    fileId: z.string().uuid(),
    /** Voice clips ship duration + waveform so the player renders without
     *  re-decoding the audio blob.  Omit for plain file / image attachments. */
    isVoice: z.boolean().default(false),
    durationSeconds: z.number().int().nonnegative().optional(),
    /** ~64 normalised peaks (0..1).  Required when isVoice=true. */
    waveform: z.array(z.number()).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.isVoice && (v.durationSeconds == null || v.waveform == null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'voice attachments require durationSeconds + waveform',
      });
    }
  });
export type CommentAttachmentInput = z.infer<typeof CommentAttachmentInputSchema>;

export const CreateCommentSchema = z
  .object({
    /** TipTap JSON. */
    body: z.unknown(),
    /** Plaintext mirror for FTS — frontend computes via TipTap → text. */
    bodyText: z.string().min(0).max(60_000),
    /** Reply to a top-level comment.  Replies cannot be replied to (one-level only). */
    parentCommentId: z.string().uuid().nullable().optional(),
    /** Mention user-ids parsed from the body by the frontend (TipTap mention nodes).
     * Server doesn't re-parse — it trusts this list and uses it for fan-out notifications.
     */
    mentionedUserIds: z.array(z.string().uuid()).default([]),
    /** Inline file / voice / image attachments — render as cards inside the
     *  rendered comment (à la Telegram), not as text links.  Files must be
     *  uploaded first via the presign endpoint at scope `{kind:'task'}` so
     *  RLS + ownership are already enforced when we join.  Plain text
     *  comments still work — leave the array empty. */
    attachments: z.array(CommentAttachmentInputSchema).default([]),
  })
  .superRefine((v, ctx) => {
    // A comment must carry SOMETHING — either text or at least one attachment.
    if ((v.bodyText?.trim().length ?? 0) === 0 && v.attachments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'comment must have body text or at least one attachment',
        path: ['bodyText'],
      });
    }
  });
export type CreateCommentInput = z.infer<typeof CreateCommentSchema>;

/** Projection — one attachment as it comes back on the comment. */
export const CommentAttachmentSchema = z.object({
  id: z.string().uuid(),
  fileId: z.string().uuid(),
  isVoice: z.boolean(),
  durationSeconds: z.number().int().nullable(),
  waveform: z.array(z.number()).nullable(),
  /** Inlined from `core.files` so the renderer doesn't need a second
   *  round-trip per attachment. */
  fileName: z.string(),
  fileMime: z.string(),
  fileSizeBytes: z.number().int(),
  /** Presigned download URL (15-min) — same pipeline as chat attachments. */
  downloadUrl: z.string().nullable(),
});
export type CommentAttachmentProjection = z.infer<typeof CommentAttachmentSchema>;

export const UpdateCommentSchema = z.object({
  body: z.unknown(),
  bodyText: z.string().min(1).max(60_000),
  mentionedUserIds: z.array(z.string().uuid()).default([]),
});
export type UpdateCommentInput = z.infer<typeof UpdateCommentSchema>;

export const CommentSchema = z.object({
  id: z.string().uuid(),
  taskId: z.string().uuid(),
  authorUserId: z.string().uuid(),
  body: z.unknown(),
  bodyText: z.string(),
  parentCommentId: z.string().uuid().nullable(),
  isActive: z.boolean(),
  /** Whether the actor is still inside the 24-h edit window for THEIR own comments. */
  canEdit: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Inline attachments (files / voice / images) — empty for plain text
   *  comments.  See CommentAttachmentSchema. */
  attachments: z.array(CommentAttachmentSchema).default([]),
});
export type CommentProjection = z.infer<typeof CommentSchema>;

export const CommentListPageSchema = z.object({
  items: z.array(CommentSchema),
  nextCursor: z.string().nullable(),
});
export type CommentListPageProjection = z.infer<typeof CommentListPageSchema>;

export const CommentsListQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type CommentsListQuery = z.infer<typeof CommentsListQuerySchema>;

// ── projection (single task — full) ──────────────────────────────────────────
export const TaskUserRightsSchema = z.object({
  canView: z.boolean(),
  canViewHistory: z.boolean(),
  canViewComments: z.boolean(),
  canViewTime: z.boolean(),
  canViewGitHub: z.boolean(),
  canEditTitle: z.boolean(),
  canEditDescription: z.boolean(),
  canEditType: z.boolean(),
  canChangePriority: z.boolean(),
  canChangeDates: z.boolean(),
  canChangeLaborHours: z.boolean(),
  canChangeStatus: z.boolean(),
  canSetDone: z.boolean(),
  canSetCancelled: z.boolean(),
  canChangeAssignee: z.boolean(),
  canChangeRespondent: z.boolean(),
  canAddCollaborator: z.boolean(),
  canRemoveCollaborator: z.boolean(),
  canSelfWatch: z.boolean(),
  canSelfUnwatch: z.boolean(),
  canAddOtherWatchers: z.boolean(),
  canRemoveOtherWatchers: z.boolean(),
  canManageTags: z.boolean(),
  canAttachMilestone: z.boolean(),
  canDetachMilestone: z.boolean(),
  canManageRelations: z.boolean(),
  canComment: z.boolean(),
  canDeleteOwnComments: z.boolean(),
  canDeleteAnyComments: z.boolean(),
  canUploadFiles: z.boolean(),
  canDeleteOwnFiles: z.boolean(),
  canDeleteAnyFiles: z.boolean(),
  canArchive: z.boolean(),
  canRestore: z.boolean(),
  canDeletePermanently: z.boolean(),
  canPin: z.boolean(),
  canStartTimer: z.boolean(),
  canLogTimeForOthers: z.boolean(),
});

export const TaskSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  teamId: z.string().uuid(),
  /** `${project.key}-${task_number}`.  Computed in API; not stored. */
  key: z.string(),
  taskNumber: z.number().int().positive(),
  title: z.string(),
  description: z.unknown().nullable(),
  descriptionText: z.string().nullable(),
  descriptionFormat: DescriptionFormat,
  type: z.string(),
  statusId: z.string(),
  /** Phase 5 chunk B — the workflow this task runs on + the active
   *  state.  NOT NULL post-migration-0082 cleanup; every task is
   *  workflow-driven.  Legacy `statusId` text key stays as the mirror
   *  until a future deprecation migration retires it. */
  workflowId: z.string().uuid(),
  workflowStateId: z.string().uuid(),
  /** Resolved from the workflow-state graph on single-task fetches (null
   *  until hydrated).  `statusCategory` is the PM bucket the active state
   *  belongs to; `statusLabel` is its human label.  Carried so AI surfaces
   *  (MCP task resource, agent context, future status-write tools) read the
   *  workflow-coherent state — not just the legacy `statusId` text key —
   *  keeping the PM model and the AI Core in sync. */
  statusCategory: z
    .enum(['open', 'in_progress', 'review', 'done', 'cancelled'])
    .nullable()
    .default(null),
  statusLabel: z.string().nullable().default(null),
  priority: z.number().int().min(1).max(4),
  authorId: z.string().uuid(),
  respondentId: z.string().uuid().nullable(),
  assigneeId: z.string().uuid().nullable(),
  /** Collaborator user ids.  The single-task projection emits this (empty
   *  until hydrated); declared here so it survives the schema parse and stays
   *  consistent with the list item + enriched client Task. */
  collaboratorIds: z.array(z.string().uuid()).default([]),
  parentTaskId: z.string().uuid().nullable(),
  milestoneId: z.string().uuid().nullable(),
  /** Resolved milestone name — same convention as TaskListItem. */
  milestoneName: z.string().nullable().default(null),
  /** Tags attached to this task (M:N).  Empty array when nothing attached. */
  tags: z.array(TaskListTagSchema).default([]),
  /** Outgoing-relations count (BLOCKS / RELATES_TO / etc.) — used for the 🔗N badge. */
  relationsCount: z.number().int().nonnegative().default(0),
  spawnedFromRecurringSpecId: z.string().uuid().nullable(),
  startDate: z.string().nullable(),
  /** Absolute UTC moment as ISO string (e.g. "2026-05-21T15:00:00.000Z").
   *  Display logic should interpret it relative to `projectTimezone`. */
  dueDate: z.string().nullable(),
  /** IANA tz of the owning project — needed to render the deadline as
   *  "21 May EOD · Europe/Kyiv" with the optional "your time" delta. */
  projectTimezone: z.string().default('UTC'),
  laborHours: z.number(),
  laborHoursSpent: z.number(),
  laborHoursSpentTimer: z.number(),
  laborHoursSpentManual: z.number(),
  completedAt: z.string().nullable(),
  pinnedInProject: z.boolean(),
  reminderAt: z.string().nullable(),
  archived: z.boolean(),
  archivedAt: z.string().nullable(),
  archivedBy: z.string().uuid().nullable(),
  /** Phase 5 chunk 6 — Incident extension.  `isIncident=false` rows
   *  carry severity=null + slaAt=null (DB CHECK enforces) and an
   *  empty ownership array. */
  isIncident: z.boolean().default(false),
  severity: IncidentSeverity.nullable(),
  slaAt: z.string().nullable(),
  ownershipChain: z.array(IncidentOwnershipSlotSchema).default([]),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Per-actor right booleans.  Frontend uses these to show/hide UI. */
  userRights: TaskUserRightsSchema,
});
export type TaskProjection = z.infer<typeof TaskSchema>;

// ── projection (list item — slimmer) ────────────────────────────────────────
// TaskListTagSchema is declared near the top of the file so TaskSchema +
// TaskListItemSchema can share it.

export const TaskListItemSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Team that owns the project.  Shipped so consumers don't need a second
   *  lookup to compute team-scoped behaviour (e.g. accent colour, IA). */
  teamId: z.string().uuid(),
  key: z.string(),
  taskNumber: z.number().int().positive(),
  title: z.string(),
  type: z.string(),
  statusId: z.string(),
  /** Bucket the task currently lives in — derived from the project's effective
   *  workflow (`projects.task_statuses ?? teams.default_task_statuses`).
   *  Lives on the list item so kanban / overview / "active vs done" filters
   *  don't have to re-resolve via project settings on every render.  Falls
   *  back to 'open' only when status metadata is missing (shouldn't happen
   *  in practice — the backend resolves it at projection time). */
  statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']),
  priority: z.number().int().min(1).max(4),
  authorId: z.string().uuid(),
  respondentId: z.string().uuid().nullable(),
  assigneeId: z.string().uuid().nullable(),
  parentTaskId: z.string().uuid().nullable(),
  milestoneId: z.string().uuid().nullable(),
  /** Resolved milestone name — null when no milestone or in lean (picker) responses. */
  milestoneName: z.string().nullable().default(null),
  /** Tag pills for list rendering — empty in lean (picker) responses. */
  tags: z.array(TaskListTagSchema).default([]),
  /** Number of outgoing relations (BLOCKS / DUPLICATES / RELATES_TO).  Drives the 🔗N badge. */
  relationsCount: z.number().int().nonnegative().default(0),
  /** Active comment count — the 💬N badge. */
  commentsCount: z.number().int().nonnegative().default(0),
  /** Attached (non-deleted) file count — the 📎N badge. */
  filesCount: z.number().int().nonnegative().default(0),
  /**
   * Checklist progress (all checklists of the task) — the "2/5" row badge.
   * Optional: lean / older responses omit them (read as 0). Filled by the
   * list hydrator in one grouped query per page (2026-10-19).
   */
  checklistTotal: z.number().int().nonnegative().optional(),
  checklistDone: z.number().int().nonnegative().optional(),
  /** Live (non-archived, non-deleted) subtasks, and how many are Done. */
  subtasksCount: z.number().int().nonnegative().optional(),
  subtasksDone: z.number().int().nonnegative().optional(),
  /** Co-executors on this task.  Used by card surfaces to render avatar pills. */
  collaboratorIds: z.array(z.string().uuid()).default([]),
  /** Watcher headcount — surfaces use this for the 👁N badge.  We deliberately
   *  don't ship the ID list on list items (privacy + card payload size). */
  watchersCount: z.number().int().nonnegative().default(0),
  startDate: z.string().nullable().optional(), // planned start (YYYY-MM-DD) — Timeline bars
  /** Absolute UTC moment as ISO string; null when no deadline. */
  dueDate: z.string().nullable(),
  /** When the task became done / cancelled (null while active) — "done last 7d"
   *  throughput without proxying via updatedAt. */
  completedAt: z.string().nullable().default(null),
  /** IANA tz of the owning project for deadline display. */
  projectTimezone: z.string().default('UTC'),
  laborHours: z.number(),
  laborHoursSpent: z.number(),
  pinnedInProject: z.boolean(),
  archived: z.boolean(),
  /** Phase 5 chunk 6 — Incident flag + severity for badge rendering on
   *  the list/board.  Full incident metadata (slaAt, ownershipChain)
   *  lives on the heavier TaskSchema. */
  isIncident: z.boolean().default(false),
  severity: IncidentSeverity.nullable().default(null),
  slaAt: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Per-actor rights snapshot — the full 38-field matrix from
   *  `TaskRightsService.computeFromFacts`.  Surfaces gate mutations using
   *  `task.userRights?.canX === false` (server stays the final arbiter; an
   *  unknown right is NOT a denial).  Closes the Pattern A defect class
   *  flagged in the 2026-05-21 permissions audit. */
  userRights: TaskUserRightsSchema,
  /** Heuristic risk score 0-100 with bucketed level + human reasons.  Computed
   *  by `RiskScoreService` in the hydration step (NO LLM — pure rules over
   *  overdue / stalled / no-assignee / priority signals).  null on
   *  done/cancelled/archived tasks where risk is meaningless. */
  riskScore: RiskScoreSchema.nullable().default(null),
});
export type TaskListItemProjection = z.infer<typeof TaskListItemSchema>;

export const TaskListPageSchema = z.object({
  items: z.array(TaskListItemSchema),
  /** Cursor for the next page; null means end of list. */
  nextCursor: z.string().nullable(),
});
export type TaskListPageProjection = z.infer<typeof TaskListPageSchema>;

// ── list query ──────────────────────────────────────────────────────────────
export const ListTasksQuerySchema = z.object({
  status: z.string().optional(),
  statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']).optional(),
  priority: z.coerce.number().int().min(1).max(4).optional(),
  assignee: z.string().uuid().optional(),
  respondent: z.string().uuid().optional(),
  author: z.string().uuid().optional(),
  watching: boolFromQuery().optional(),
  archived: boolFromQuery().default(false),
  /** Phase 5 chunk 6 — Incident filter.  `true` returns only rows with
   *  is_incident=TRUE; `false` excludes incidents; omit to mix both. */
  incident: boolFromQuery().optional(),
  /** Filter by severity (sev1..sev4); implies `incident=true` server-side. */
  severity: IncidentSeverity.optional(),
  q: z.string().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  cursor: z.string().optional(), // base64({id, ts})
  sort: z.enum(['createdAt', 'updatedAt', 'priority', 'dueDate']).default('createdAt'),
});
export type ListTasksQuery = z.infer<typeof ListTasksQuerySchema>;

/**
 * Cross-team My Tasks query — same shape as project list minus project-specific
 * filters.  The endpoint returns every task where the actor is author /
 * respondent / assignee / collaborator across ALL teams they belong to,
 * honouring OUTSTAFF self-only filtering centrally.
 */
export const MyTasksQuerySchema = z.object({
  statusCategory: z.enum(['open', 'in_progress', 'review', 'done', 'cancelled']).optional(),
  priority: z.coerce.number().int().min(1).max(4).optional(),
  archived: boolFromQuery().default(false),
  /** Phase 5 chunk 6 — Incident filter (same semantics as the
   *  project-scoped list). */
  incident: boolFromQuery().optional(),
  severity: IncidentSeverity.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  cursor: z.string().optional(),
  sort: z.enum(['createdAt', 'updatedAt', 'priority', 'dueDate']).default('updatedAt'),
});
export type MyTasksQuery = z.infer<typeof MyTasksQuerySchema>;

void ColorHex; // reserved for future tag-related DTOs in this file
